import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogCancel } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { createSite, updateSite } from '@/lib/api/endpoints/organizations';
import { mergeFieldErrors, trimToNull, type LocalFieldErrors } from '@/lib/form-errors';
import type { Organization, Site } from '@/lib/api/types';

/*
  Create or edit a site.

  A site belongs to exactly one organization and that parent is chosen at creation and then
  fixed. There is no "move site to another organization" here, deliberately: a site carries
  devices and device groups, and every role assignment scoped to any of them would silently
  start or stop granting access. That is a migration, not a form field. Moving the devices is
  the supported path, and the device move dialog does it one machine at a time with a warning.

  So on an edit the organization is shown as read-only text rather than as a disabled select.
  A disabled select looks like something that might become enabled.

  Timezone is required and defaults to the browser's. It decides when a maintenance window falls
  and when "overnight" is, so an unset timezone is a reboot at the wrong hour rather than a
  cosmetic gap. The list is short and named; a full IANA picker is not Phase 1 work, and the
  field accepts a typed value the server validates.
*/

/** Common zones for a North American MSP, plus UTC. The server accepts any IANA name. */
const TIMEZONE_OPTIONS: readonly string[] = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Dublin',
  'UTC',
];

function defaultTimezone(): string {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return resolved === '' ? 'UTC' : resolved;
  } catch {
    return 'UTC';
  }
}

export interface SiteFormDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The parent. Fixed for the life of the site. */
  readonly organization: Organization;
  /** Null to create, a site to edit. */
  readonly site: Site | null;
}

export function SiteFormDialog({
  open,
  onOpenChange,
  organization,
  site,
}: SiteFormDialogProps): React.JSX.Element {
  const formId = `site-form-${useId()}`;
  const queryClient = useQueryClient();
  const editing = site !== null;

  const [name, setName] = useState(site?.name ?? '');
  const [addressLine1, setAddressLine1] = useState(site?.addressLine1 ?? '');
  const [city, setCity] = useState(site?.city ?? '');
  const [region, setRegion] = useState(site?.region ?? '');
  const [postalCode, setPostalCode] = useState(site?.postalCode ?? '');
  const [countryCode, setCountryCode] = useState(site?.countryCode ?? 'US');
  const [timezone, setTimezone] = useState(site?.timezone ?? defaultTimezone());
  const [local, setLocal] = useState<LocalFieldErrors>({});

  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setName(site?.name ?? '');
      setAddressLine1(site?.addressLine1 ?? '');
      setCity(site?.city ?? '');
      setRegion(site?.region ?? '');
      setPostalCode(site?.postalCode ?? '');
      setCountryCode(site?.countryCode ?? 'US');
      setTimezone(site?.timezone ?? defaultTimezone());
      setLocal({});
    }
  }

  const mutation = useMutation({
    mutationFn: async (): Promise<Site> => {
      const payload = {
        name: name.trim(),
        addressLine1: trimToNull(addressLine1),
        city: trimToNull(city),
        region: trimToNull(region),
        postalCode: trimToNull(postalCode),
        countryCode: countryCode.trim() === '' ? null : countryCode.trim().toUpperCase(),
        timezone: timezone.trim(),
      };
      if (site === null) {
        return createSite({ ...payload, organizationId: organization.id });
      }
      return updateSite(site.id, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      onOpenChange(false);
    },
  });

  const errors = mergeFieldErrors(local, mutation.error);

  /**
   * Clears the previous failure as soon as a field is edited, so a stale rejection does not sit
   * under a value that has since changed. Same reasoning as OrganizationFormDialog.
   */
  function clearSubmitError(): void {
    if (mutation.isError) {
      mutation.reset();
    }
    if (Object.keys(local).length > 0) {
      setLocal({});
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (name.trim() === '') {
      found.name = 'A name is required, and it has to be unique within this organization.';
    }
    if (timezone.trim() === '') {
      found.timezone = 'A timezone is required. It decides when a maintenance window falls.';
    }
    const country = countryCode.trim();
    if (country !== '' && !/^[A-Za-z]{2}$/.test(country)) {
      found.countryCode = 'Two letters, for example US or GB. Leave it empty if you would rather not say.';
    }
    setLocal(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    mutation.mutate();
  }

  const timezoneOptions = TIMEZONE_OPTIONS.includes(timezone)
    ? TIMEZONE_OPTIONS
    : [timezone, ...TIMEZONE_OPTIONS];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `Edit ${site.name}` : `New site at ${organization.name}`}
      description="A site is a physical location. Device groups and role assignments can be scoped to one."
      busy={mutation.isPending}
      footer={
        <>
          <DialogCancel disabled={mutation.isPending} />
          <Button type="submit" form={formId} variant="primary" loading={mutation.isPending}>
            {editing ? 'Save changes' : 'Create site'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wide text-fg-muted uppercase">
            Organization
          </p>
          <p className="mt-0.5 text-sm text-fg">{organization.name}</p>
          <p className="mt-0.5 text-xs text-fg-subtle">
            {editing
              ? 'A site cannot be moved between organizations. Move its devices instead, one at a time, so the access change is visible.'
              : 'Fixed once the site exists.'}
          </p>
        </div>

        <Field
          label="Site name"
          required
          autoFocus
          value={name}
          maxLength={120}
          autoComplete="off"
          hint="Unique within this organization. Main Clinic, Shop Floor, Warehouse 2."
          onChange={(event) => {
            clearSubmitError();
            setName(event.target.value);
          }}
          {...(errors.forField('name') === undefined ? {} : { error: errors.forField('name') })}
        />

        <Select
          label="Timezone"
          value={timezone}
          options={timezoneOptions.map((zone) => ({ value: zone, label: zone }))}
          onValueChange={setTimezone}
        />
        {errors.forField('timezone') === undefined ? (
          <p className="-mt-2 text-xs text-fg-subtle">
            Maintenance windows and overnight reboots are calculated in this zone.
          </p>
        ) : (
          <p role="alert" className="-mt-2 text-xs font-medium text-[var(--color-danger)]">
            {errors.forField('timezone')}
          </p>
        )}

        <Field
          label="Address"
          value={addressLine1}
          maxLength={200}
          autoComplete="off"
          hint="Optional. Useful when a technician has to send someone on site."
          onChange={(event) => {
            clearSubmitError();
            setAddressLine1(event.target.value);
          }}
          {...(errors.forField('addressLine1') === undefined
            ? {}
            : { error: errors.forField('addressLine1') })}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="City"
            value={city}
            maxLength={80}
            autoComplete="off"
            onChange={(event) => {
              clearSubmitError();
              setCity(event.target.value);
            }}
            {...(errors.forField('city') === undefined ? {} : { error: errors.forField('city') })}
          />
          <Field
            label="Region"
            value={region}
            maxLength={80}
            autoComplete="off"
            onChange={(event) => {
              clearSubmitError();
              setRegion(event.target.value);
            }}
            {...(errors.forField('region') === undefined ? {} : { error: errors.forField('region') })}
          />
          <Field
            label="Postal code"
            value={postalCode}
            maxLength={20}
            autoComplete="off"
            onChange={(event) => {
              clearSubmitError();
              setPostalCode(event.target.value);
            }}
            {...(errors.forField('postalCode') === undefined
              ? {}
              : { error: errors.forField('postalCode') })}
          />
        </div>

        <Field
          label="Country code"
          value={countryCode}
          maxLength={2}
          autoComplete="off"
          className="w-24 font-mono uppercase"
          hint="ISO 3166-1 alpha-2."
          onChange={(event) => {
            clearSubmitError();
            setCountryCode(event.target.value.toUpperCase());
          }}
          {...(errors.forField('countryCode') === undefined
            ? {}
            : { error: errors.forField('countryCode') })}
        />

        {organization.status !== 'active' ? (
          <Alert tone="warning" title={`${organization.name} is ${organization.status}`}>
            A site can still be created or edited, but devices at a{' '}
            {organization.status} organization are not expected to connect.
          </Alert>
        ) : null}

        {mutation.isError && !errors.allErrorsAreFieldLevel ? (
          <ApiErrorAlert
            error={mutation.error}
            context={editing ? 'Could not save the site' : 'Could not create the site'}
          />
        ) : null}
      </form>
    </Dialog>
  );
}
